/**
 * Renders optional checklist-level bullet reminders (dot points).
 */

import React, { memo } from "react";
import { clsx } from "clsx";

export function checklistBulletPointsTrimmed(
  items: string[] | undefined | null,
): string[] {
  return (items ?? []).map((s) => s.trim()).filter(Boolean);
}

interface ChecklistBulletPointsProps {
  items: string[] | undefined | null;
  className?: string;
  /** Screen-reader / layout label */
  heading?: string;
  compact?: boolean;
}

function ChecklistBulletPointsInner({
  items,
  className,
  heading = "Key points",
  compact = false,
}: ChecklistBulletPointsProps) {
  const trimmed = checklistBulletPointsTrimmed(items);
  if (trimmed.length === 0) return null;

  return (
    <div
      className={clsx(
        "rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-800/50 overflow-visible",
        compact ? "p-2.5" : "p-4",
        className,
      )}
    >
      <div
        className={clsx(
          "font-medium text-slate-600 dark:text-slate-300",
          compact ? "text-[10px] uppercase tracking-wide mb-1.5" : "text-xs mb-2",
        )}
      >
        {heading}
      </div>
      <ul
        className={clsx(
          "list-disc ps-5 text-slate-700 dark:text-slate-200 marker:text-slate-400 overflow-visible [list-style-position:outside]",
          compact ? "text-xs space-y-0.5" : "text-sm space-y-1",
        )}
      >
        {trimmed.map((line, i) => (
          <li key={i}>{line}</li>
        ))}
      </ul>
    </div>
  );
}

export const ChecklistBulletPoints = memo(ChecklistBulletPointsInner);
